import {
  Dispatch,
  DOMAttributes,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  type JSX,
} from 'react';
import { IndexableObject } from '../IndexableObject';

export interface SharedActiveProps
  extends Pick<
    DOMAttributes<HTMLElement>,
    'onMouseDown' | 'onBlur' | 'onKeyDown' | 'onKeyUp'
  > {
  active?: boolean;
}

export type ReturnedActiveProps = SharedActiveProps &
  Required<Pick<SharedActiveProps, 'active'>>;

const keyTogglesActive = ({ key }: { key: string }) =>
  key === 'Enter' || key === ' ';

export function useActive({
  active,
  onMouseDown,
  onKeyDown,
  onKeyUp,
  onBlur,
}: SharedActiveProps): ReturnedActiveProps {
  const [activeState, setActiveState]: [
    boolean,
    Dispatch<SetStateAction<boolean>>,
  ] = useState<boolean>(false);

  const handleMouseDown = useCallback(
    (evt: MouseEvent<HTMLElement>): void => {
      if (onMouseDown) onMouseDown(evt);
      setActiveState(true);
    },
    [onMouseDown]
  );

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLElement>): void => {
      if (onKeyDown) onKeyDown(evt);
      if (keyTogglesActive(evt)) setActiveState(true);
    },
    [onKeyDown]
  );

  const handleKeyUp = useCallback(
    (evt: KeyboardEvent<HTMLElement>): void => {
      if (onKeyUp) onKeyUp(evt);
      if (keyTogglesActive(evt)) setActiveState(false);
    },
    [onKeyUp]
  );

  const handleBlur = useCallback(
    (evt: FocusEvent<HTMLElement>): void => {
      if (onBlur) onBlur(evt);
      setActiveState(false);
    },
    [onBlur]
  );

  // After `mousedown` on the wrapped element, `mouseup` may occur elsewhere in the document.
  useEffect(() => {
    if (!activeState) return undefined;
    const handleMouseUp = (): void => {
      setActiveState(false);
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeState]);

  return {
    active: active != null ? active : activeState,
    onMouseDown: activeState ? onMouseDown : handleMouseDown,
    onKeyDown: activeState ? onKeyDown : handleKeyDown,
    onKeyUp: activeState ? handleKeyUp : onKeyUp,
    onBlur: activeState ? handleBlur : onBlur,
  };
}

export interface ActiveProviderProps
  extends SharedActiveProps,
    IndexableObject {
  children: ({
    active,
    onMouseDown,
    onBlur,
    onKeyDown,
    onKeyUp,
  }: SharedActiveProps) => JSX.Element;
}

export const ActiveProvider = (props: ActiveProviderProps): JSX.Element => {
  return props.children(useActive(props));
};

ActiveProvider.displayName = 'ActiveProvider';
